<template>
  <PageBanner>
    <template #left-input>
      <LocationFilter
        ref="locationFilterRef"
        :initial-state="props.initialState"
        :initial-city="props.initialCity"
        :pre-fetched-cities="props.preFetchedCities"
        :states-cities="props.statesCities"
        :permalink="props.permalink"
        @update:state="handleStateSelect"
        @update:state-input="handleStateInput"
        @update:city="handleCitySelect"
        @update:city-input="handleCityInput"
        @update:state-exact-match="handleStateExactMatch"
        @update:city-exact-match="handleCityExactMatch"
      />
    </template>
    <template #right-input>
      <RoleAutocomplete
        ref="roleAutocomplete"
        :permalink="props.permalink"
        :initial-role="props.initialRole"
        :mode="props.mode"
        @update:selected="handleRoleSelect"
        @update:input="handleRoleInput"
        @update:exact-match="handleExactMatch"
      />
    </template>
    <template #action>
      <ClientOnly>
        <NuxtLink
          :to="filterUrl"
          :class="[
            'rounded-lg px-4 py-2 text-center text-sm font-medium',
            isValidSelection
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          ]"
          :aria-disabled="!isValidSelection"
          @click.prevent="!isValidSelection ? null : true"
        >
          Apply Filters
        </NuxtLink>
      </ClientOnly>
    </template>
  </PageBanner>
</template>

<script setup lang="ts">
import { ref, computed, reactive } from 'vue'
import RoleAutocomplete from '../filters/RoleAutocomplete.vue'
import LocationFilter from '../filters/LocationFilter.vue'
import type { City } from '../filters/CityAutocomplete.vue'
import { stateOptions } from '~/utils/states'
import PageBanner from '~/components/common/PageBanner.vue'
import { buildCompanyUrl } from '~/utils/formatters'

const props = defineProps({
  mode: {
    type: String as () => 'reviews' | 'salaries',
    default: 'reviews',
    validator: (value: string): boolean =>
      ['reviews', 'salaries'].includes(value)
  },
  permalink: {
    type: String,
    required: true
  },
  initialState: {
    type: String,
    default: ''
  },
  initialCity: {
    type: String,
    default: ''
  },
  initialRole: {
    type: String,
    default: ''
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  preFetchedCities: {
    type: Array as PropType<City[]>,
    default: () => []
  },
  statesCities: {
    type: Object as PropType<Record<string, City[]>>,
    default: () => ({})
  }
})
const emit = defineEmits(['filter'])
const roleAutocomplete = ref<InstanceType<typeof RoleAutocomplete> | null>(
  null
)
const locationFilterRef = ref<InstanceType<typeof LocationFilter> | null>(null)
const selectedRole = ref(null)
const selectedState = ref(
  props.initialState
    ? stateOptions.find(s => s.code === props.initialState)
    : null
)
const roleIsValid = ref(true)
const stateIsValid = ref(true)
const cityIsValid = ref(true)
const filters = reactive({
  role: props.initialRole || '',
  state: props.initialState || '',
  city: props.initialCity || ''
})
const originalFilters = reactive({
  role: props.initialRole || '',
  state: props.initialState || '',
  city: props.initialCity || ''
})
const isValidSelection = computed(() => {
  const hasChanges
    = filters.role !== originalFilters.role
      || filters.state !== originalFilters.state
      || filters.city !== originalFilters.city
  if (!hasChanges) return false
  if (filters.role.trim() && !roleIsValid.value) return false
  if (filters.state.trim() && !stateIsValid.value) return false
  if (filters.city.trim()) {
    if (!cityIsValid.value) return false
    if (!filters.state.trim() || !stateIsValid.value) return false
  }
  return true
})
function handleRoleSelect(role) {
  selectedRole.value = role
  filters.role = role?.popular_title || role?.title || ''
  roleIsValid.value = !!role
}
function handleRoleInput(value) {
  if (typeof value !== 'string') {
    value = String(value)
  }
  filters.role = value
  if (!filters.role && selectedRole.value) {
    selectedRole.value = null
  }
}
function handleExactMatch(hasMatch) {
  roleIsValid.value = hasMatch
}
function handleStateSelect(state) {
  selectedState.value = state
  filters.state = state?.code || ''
  stateIsValid.value = !!state
  if (!state) {
    filters.city = ''
    cityIsValid.value = true
  }
}
function handleStateInput(value) {
  filters.state = value
  if (!value) {
    selectedState.value = null
    filters.city = ''
    cityIsValid.value = true
  }
}
function handleCitySelect(city) {
  filters.city = city ? city.name : ''
  cityIsValid.value = !!city
}
function handleCityInput(value) {
  filters.city = value
}
function handleStateExactMatch(hasMatch) {
  stateIsValid.value = hasMatch
  if (!hasMatch && filters.city) {
    filters.city = ''
    cityIsValid.value = true
  }
}
function handleCityExactMatch(hasMatch) {
  cityIsValid.value = hasMatch
}
const filterUrl = computed(() => {
  const roleTitle = selectedRole.value?.popular_title?.trim() || selectedRole.value?.title?.trim()
  const stateCode = selectedState.value?.code
  const city = filters.city?.trim()

  return buildCompanyUrl(props.permalink, props.mode, {
    city: city || undefined,
    stateCode: stateCode || undefined,
    role: roleTitle || undefined
  })
})
</script>
